import type { ReactNode } from 'react';
import { Shield, Lock } from 'lucide-react';
import {
  getAccountState,
  getPaymentTier,
  TIER_LABELS,
  type AdminUserEntry,
  type PaymentTier,
} from '../../lib/adminUserView';

const TIER_CLASSES: Record<PaymentTier, string> = {
  ent_admin: 'bg-purple-100 text-purple-800',
  ent: 'bg-purple-100 text-purple-800',
  subscribed: 'bg-green-100 text-green-800',
  ppu: 'bg-gray-100 text-gray-700',
};

function Badge({ cls, children }: { cls: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

/**
 * Status badges for one user: Admin for platform admins, otherwise the payment
 * tier (PPU / Subscribed / Ent / Ent (admin)), plus a Locked or Deleted overlay
 * badge when the account is in that state.
 */
export default function UserStatusBadges({ user }: { user: AdminUserEntry }) {
  const state = getAccountState(user);
  const tier = getPaymentTier(user);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {user.role === 'admin' ? (
        <Badge cls="bg-blue-100 text-blue-800">
          <Shield className="w-3 h-3" />
          Admin
        </Badge>
      ) : (
        <Badge cls={TIER_CLASSES[tier]}>{TIER_LABELS[tier]}</Badge>
      )}
      {state === 'locked' && (
        <Badge cls="bg-amber-100 text-amber-800">
          <Lock className="w-3 h-3" />
          Locked
        </Badge>
      )}
      {state === 'deleted' && <Badge cls="bg-red-100 text-red-800">Deleted</Badge>}
    </span>
  );
}
